<script setup>
import { computed } from 'vue';
import { messageStamp } from 'shared/helpers/timeHelper';

import MessageStatus from './MessageStatus.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';
import { useI18n } from 'vue-i18n';

import { MESSAGE_STATUS, MESSAGE_TYPES, SENDER_TYPES } from './constants';
import { escapeHtml } from 'shared/helpers/HTMLSanitizer';

const {
  isAFacebookInbox,
  isALineChannel,
  isAPIInbox,
  isASmsInbox,
  isATelegramChannel,
  isATwilioChannel,
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAnEmailChannel,
  isAnInstagramChannel,
  isATiktokChannel,
} = useInbox();

const {
  status,
  groupDisplayStatus,
  isPrivate,
  createdAt,
  sourceId,
  messageType,
  contentAttributes,
  sender,
} = useMessageContext();

// Eltafouk: same-minute messages are rendered as one group and only the last
// one's tick is shown. MessageList passes the weakest delivery status in the
// group via `groupDisplayStatus`, so the single displayed tick reflects the
// least-delivered message (e.g. stays "delivered" until every message is read)
// and never over-claims. Falls back to the message's own status for standalone
// or uniform groups.
const displayStatus = computed(() => groupDisplayStatus?.value ?? status.value);

const { t } = useI18n();

// MessageList pipes messages through useCamelCase, so the mirror's
// `eltafouk_agent_name` arrives here as `eltafoukAgentName`. Read both keys via
// dynamic indexing so the bundler can't statically drop a branch — same pattern
// as bubbles/Text/Index.vue.
const pickAttr = (ca, ...keys) => {
  const hit = keys.find(k => ca[k] !== undefined && ca[k] !== null);
  return hit ? ca[hit] : undefined;
};

const agentNameToShow = computed(() => {
  // Eltafouk: a reply authored by a specific agent (via the panel or the relay
  // fallback) carries the real author in content_attributes — prefer it over the
  // posting identity (page-relay «الصفحة» / admin) so each outgoing message
  // shows the agent who actually sent it.
  const stamped = pickAttr(
    contentAttributes.value || {},
    'eltafoukAgentName',
    'eltafouk_agent_name'
  );
  if (typeof stamped === 'string' && stamped.length > 0) {
    return stamped.split(' ')[0];
  }
  if (!sender?.value || sender.value.type === SENDER_TYPES.AGENT_BOT) {
    return t('CONVERSATION.BOT');
  }
  if (sender.value.name) {
    return sender.value.name.split(' ')[0];
  }
  return '';
});

const showAgentName = computed(() => {
  return (
    messageType.value === MESSAGE_TYPES.OUTGOING && !!agentNameToShow.value
  );
});

// Eltafouk: automated replies (greeting, button-flow tree, out-of-office,
// CSAT, email-collect) are all message_type :template and have no human
// sender. Show a «بوت» tag next to the timestamp so agents can tell an
// auto-reply apart from a human agent reply. Held as a plain Arabic literal,
// matching the other Eltafouk labels in this file (panel is Arabic-only).
const isBotMessage = computed(
  () => messageType.value === MESSAGE_TYPES.TEMPLATE
);
const botLabel = 'بوت';

const readableTime = computed(() => messageStamp(createdAt.value, 'h:mm a'));

const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  // Don't show status for failed messages, we already show error message
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  // Don't show status for deleted messages
  if (contentAttributes.value?.deleted) return false;

  if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
  if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

  return false;
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;

  // Messages will be marked as sent for the Email channel if they have a source ID.
  if (isAnEmailChannel.value) return !!sourceId.value;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isASmsInbox.value ||
    isATelegramChannel.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && displayStatus.value === MESSAGE_STATUS.SENT;
  }

  // All messages will be mark as sent for the Line channel, as there is no source ID.
  if (isALineChannel.value) return true;

  return false;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isASmsInbox.value ||
    isAFacebookInbox.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && displayStatus.value === MESSAGE_STATUS.DELIVERED;
  }
  // All messages marked as delivered for the web widget inbox and API inbox once they are sent.
  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return displayStatus.value === MESSAGE_STATUS.SENT;
  }
  if (isALineChannel.value) {
    return displayStatus.value === MESSAGE_STATUS.DELIVERED;
  }

  return false;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && displayStatus.value === MESSAGE_STATUS.READ;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return displayStatus.value === MESSAGE_STATUS.READ;
  }

  return false;
});

const statusToShow = computed(() => {
  if (isRead.value) return MESSAGE_STATUS.READ;
  if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
  if (isSent.value) return MESSAGE_STATUS.SENT;

  return MESSAGE_STATUS.PROGRESS;
});

// Eltafouk: outgoing messages that were relayed from FB/IG UI (the page
// replied directly on Facebook/Instagram, not from Chatwoot). The n8n
// workflow tags them with content_attributes.external_source = 'fb_page_ui'.
const isFromFbPageUi = computed(
  () => contentAttributes.value?.externalSource === 'fb_page_ui'
);
const fbPageUiLabel = '↩ رد من الصفحة';
const fbPageUiTooltip =
  'هذا الرد أُرسل من واجهة فيسبوك/انستجرام مباشرة، وليس من Chatwoot';

const isEdited = computed(() => !!contentAttributes.value?.previousContent);
const editedLabel = '✎ تم التعديل';

// All previous versions of the note (oldest first). Falls back to a single
// entry built from previousContent for channel edits that don't track history.
const editHistory = computed(() => {
  const history = contentAttributes.value?.editHistory;
  if (Array.isArray(history) && history.length) return history;
  const prev = contentAttributes.value?.previousContent;
  return prev ? [{ content: prev }] : [];
});

const editHistoryHtml = computed(() =>
  editHistory.value
    .map(entry => {
      const editedAt = entry.editedAt ?? entry.edited_at;
      const editorName = entry.editorName ?? entry.editor_name;
      const meta = [
        editorName,
        editedAt ? messageStamp(editedAt, 'MMM d, h:mm a') : '',
      ]
        .filter(Boolean)
        .join(' · ');
      const metaHtml = meta
        ? `<div style="font-size:10px;color:#9ca3af;margin-top:3px">${escapeHtml(meta)}</div>`
        : '';
      return `<div style="padding:7px 0;border-bottom:1px solid #f1f5f9"><div style="font-size:13px;color:#111827;line-height:1.6;word-break:break-word">${escapeHtml(entry.content ?? '')}</div>${metaHtml}</div>`;
    })
    .join('')
);

const editHistoryTitle = computed(() =>
  editHistory.value.length > 1
    ? t('CONVERSATION.NOTE_EDIT.HISTORY_TITLE')
    : t('CONVERSATION.NOTE_EDIT.ORIGINAL')
);

const editedTooltip = computed(() => ({
  content: `<div dir="rtl" style="text-align:right;padding:10px 14px;min-width:160px;max-width:260px"><div style="font-size:10px;font-weight:700;color:#9ca3af;margin-bottom:8px;letter-spacing:0.06em;text-transform:uppercase">${editHistoryTitle.value}</div>${editHistoryHtml.value}</div>`,
  html: true,
  placement: 'bottom-start',
  triggers: ['click'],
  theme: 'dropdown',
  autoHide: true,
  distance: 6,
  skidding: -4,
}));
</script>

<template>
  <div class="text-[11px] flex items-center gap-1">
    <div class="inline">
      <time dir="ltr" class="inline">{{ readableTime }}</time>
    </div>
    <span v-if="showAgentName" class="inline opacity-75">
      {{ `· ${agentNameToShow}` }}
    </span>
    <span v-if="isBotMessage" class="inline opacity-75">
      {{ `· ${botLabel}` }}
    </span>
    <span
      v-if="isFromFbPageUi"
      :title="fbPageUiTooltip"
      class="inline-flex items-center gap-0.5 rounded-md bg-[#1877F2]/10 px-1.5 py-px text-[10px] font-semibold leading-none text-[#1877F2]"
    >
      {{ fbPageUiLabel }}
    </span>
    <span
      v-if="isEdited"
      v-tooltip="editedTooltip"
      class="inline-flex items-center cursor-pointer select-none rounded-full bg-[#00C853] px-2 py-0.5 text-[10px] font-bold leading-none text-white shadow-sm shadow-green-400/40 transition-all duration-150 hover:brightness-110 active:scale-95"
    >
      {{ editedLabel }}
    </span>
    <Icon v-if="isPrivate" icon="i-lucide-lock-keyhole" class="size-3" />
    <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
  </div>
</template>
